import CompetitorFloatingView from "./index.vue";
import Vue from "vue";
import { ApiResponse } from "@/api/types";

interface Data {
  loadingCompaniesIds: number[];
  addedCompanies: Application.Company[];
}
interface Methods {
  initialize: () => void;
  initCompanies: () => void;
  closeView: () => void;
  handleInitCompanies: () => void;
  handleCloseCompetitorFloatingView: () => void;
  handleDeleteCompetitor: (company: Application.Company) => void;
  handleAddCompetitor: (company: Application.Company) => void;
  handleAddConfirmationCompetitor: (
    newValue: Application.CompetitorEmbedded[],
    oldValue: Application.CompetitorEmbedded[]
  ) => void;
}
interface Computed {
  companiesTotal: Application.CompaniesTotal;
  companies: Application.Company[];
  applicationPlan: Application.PlanDetail | undefined;
  competitors: Application.Competitor[] | undefined;
  isVisible: boolean;
  partnerUser: ApiResponse.PartnerUser;
  planId: number;
  applicationCompetitorEmbeddeds: Application.CompetitorEmbedded[];
}
interface Props {}

export default Vue.extend<Data, Methods, Computed, Props>({
  data() {
    return {
      loadingCompaniesIds: [],
      addedCompanies: []
    };
  },
  computed: {
    companiesTotal() {
      return this.$store.state.pages.plan.companiesTotal;
    },
    companies() {
      return this.$store.state.pages.plan.companies;
    },
    applicationPlan() {
      return this.$store.state.pages.plan.applicationPlan;
    },
    competitors() {
      const applicationPlan = this.applicationPlan;
      if (!applicationPlan) {
        return;
      }
      return applicationPlan.competitors;
    },
    isVisible() {
      return this.$store.state.pages.plan.isCompetitorViewVisible;
    },
    partnerUser() {
      return this.$store.state.domains.user.partnerUser;
    },
    planId() {
      return Number(this.$route.params["planId"]);
    },
    applicationCompetitorEmbeddeds() {
      return this.$store.state.pages.plan.applicationCompetitorEmbeddeds;
    }
  },
  watch: {
    isVisible(value) {
      if (value) {
        this.initialize();
      }
    },
    applicationCompetitorEmbeddeds(newValue, oldValue) {
      this.handleAddConfirmationCompetitor(newValue, oldValue);
    }
  },
  methods: {
    initialize() {
      this.initCompanies();
    },
    initCompanies() {
      this.$store.commit("pages/plan/initCompanyList", {
        companiesTotal: 0,
        companies: []
      });
    },
    closeView() {
      this.$store.commit("pages/plan/updateCompetitorView", {
        isVisible: false
      });
      this.$store.commit("pages/plan/competitorSearchParams", {
        params: {
          company_name: undefined,
          limit: 20,
          offset: 0,
          sort: ""
        }
      });

      this.$emit("close", this.addedCompanies);
    },
    handleInitCompanies() {
      this.initCompanies();
    },
    handleCloseCompetitorFloatingView() {
      this.closeView();
    },
    async handleDeleteCompetitor(company) {
      this.loadingCompaniesIds.push(company.id);
      const competitor = this.applicationCompetitorEmbeddeds.find(
        competitorEmbedded => {
          if (competitorEmbedded.company.id === company.id) {
            return competitorEmbedded;
          }
        }
      );

      if (!competitor) {
        return;
      }

      await this.$store
        .dispatch("domains/plan/deleteCompetitor", {
          partnerId: this.partnerUser.partner.id,
          planId: this.applicationPlan!.id,
          competitorId: competitor.id
        })
        .then(() => {
          this.$store.commit("pages/plan/deleteCompetitorEmbedded", {
            competitorId: competitor.id
          });
        });
      await this.$store.dispatch("domains/plan/getPlan", {
        partner_id: this.partnerUser.partner.id,
        plan_id: this.planId
      });

      this.loadingCompaniesIds = this.loadingCompaniesIds.filter(
        companiesId => companiesId !== company.id
      );
    },
    async handleAddCompetitor(company) {
      this.loadingCompaniesIds.push(company.id);
      if (!this.partnerUser) {
        return;
      }
      await this.$store.dispatch("domains/plan/createCompetitor", {
        partnerId: this.partnerUser.partner.id,
        planId: this.planId,
        company: {
          id: company.id
        }
      });

      this.addedCompanies = [...this.addedCompanies, company];

      const targetCompetitor = this.companies!.find(
        competitor => competitor.id === company.id
      );

      if (!targetCompetitor) return;
    },
    // NOTE: 企業の削除は他社のイベント読み込み後でないとエラーが出るため、
    //       handleAddConfirmationCompetitorでローディング完了処理を行う
    handleAddConfirmationCompetitor(newValue, oldValue) {
      if (!oldValue.length) {
        const index = this.loadingCompaniesIds.indexOf(newValue[0].company.id);
        this.loadingCompaniesIds.splice(index, 1);
      } else if (newValue.length > oldValue.length) {
        const diff = newValue.filter(
          (value1: Application.CompetitorEmbedded) => {
            const notMatch = oldValue.filter(
              (value2: Application.CompetitorEmbedded) =>
                value1.id !== value2.id
            );
            return notMatch.length !== 0;
          }
        );

        diff.forEach((competitor: Application.CompetitorEmbedded) => {
          this.loadingCompaniesIds = this.loadingCompaniesIds.filter(
            companiesId => companiesId !== competitor.company.id
          );
        });
      }
    }
  },
  render(createElement) {
    return createElement(CompetitorFloatingView, {
      props: {
        isVisible: this.isVisible,
        companiesTotal: this.companiesTotal,
        companies: this.companies,
        competitors: this.competitors,
        loadingCompaniesIds: this.loadingCompaniesIds
      },
      on: {
        initCompanies: this.handleInitCompanies,
        closeCompetitorFloatingView: this.handleCloseCompetitorFloatingView,
        deleteCompetitor: this.handleDeleteCompetitor,
        addCompetitor: this.handleAddCompetitor
      }
    });
  }
});
